import { gsap } from 'gsap';
import prefersReducedMotion from '../utils/prefers-reduced-motion';

/**
 * Animated Steps
 */

const SELECTOR = '.animated-steps';

const initWheel = (root) => {
	const highlight = root.querySelector('.animated-steps__highlight');
	const segments = Array.from(
		root.querySelectorAll('.animated-steps__segment')
	);
	const badges = Array.from(root.querySelectorAll('.animated-steps__badge'));
	const copyItems = Array.from(
		root.querySelectorAll('.animated-steps__copy-item')
	);
	const centreImg = root.querySelector('.animated-steps__centre img');

	const count = copyItems.length || segments.length;
	if (!count) {
		return;
	}

	const autoplay = root.dataset.autoplay === '1';
	const speed = parseFloat(root.dataset.speed) || 5;
	const stepAngle = 360 / count;
	const desktopMQ = window.matchMedia('(min-width: 992px)');

	let current = -1;
	let timer = null;
	let userStopped = false;
	let highlightRotation = 0;
	let transitionTl = null;

	const stopTimer = () => {
		if (timer) {
			timer.kill();
			timer = null;
		}
	};

	const startTimer = () => {
		if (
			!autoplay ||
			userStopped ||
			prefersReducedMotion() ||
			!desktopMQ.matches
		) {
			return;
		}
		stopTimer();
		timer = gsap.delayedCall(speed, () => {
			setActive(current + 1);
			startTimer();
		});
	};

	const setActive = (index, { animate = true } = {}) => {
		const next = ((index % count) + count) % count;
		if (next === current) {
			return;
		}
		const previous = current;
		current = next;

		// Honour reduced-motion for every transition.
		const shouldAnimate = animate && !prefersReducedMotion();

		segments.forEach((seg, i) =>
			seg.classList.toggle('is-active', i === current)
		);
		badges.forEach((badge, i) =>
			badge.classList.toggle('is-active', i === current)
		);
		copyItems.forEach((item, i) =>
			item.classList.toggle('is-active', i === current)
		);

		if (previous !== -1) {
			const delta = (((current - previous) % count) + count) % count;
			highlightRotation += delta * stepAngle;
		} else {
			highlightRotation = current * stepAngle;
		}

		// First mount or reduced-motion: snap everything into place, no timeline.
		if (!shouldAnimate) {
			if (transitionTl) {
				transitionTl.kill();
				transitionTl = null;
			}
			if (highlight) {
				gsap.set(highlight, {
					rotation: highlightRotation,
					svgOrigin: '200 200',
				});
			}
			copyItems.forEach((item, i) =>
				gsap.set(item, { autoAlpha: i === current ? 1 : 0, y: 0 })
			);
			// Clear any residual scale a killed centre pulse may have left behind.
			if (centreImg) {
				gsap.set(centreImg, { scale: 1 });
			}
			return;
		}

		if (transitionTl) {
			transitionTl.kill();
		}
		transitionTl = gsap.timeline({
			defaults: { ease: 'power2.out' },
			// Release the finished timeline (and its captured references).
			onComplete: () => {
				transitionTl = null;
			},
		});

		copyItems.forEach((item, i) => {
			if (i !== current && i !== previous) {
				gsap.set(item, { autoAlpha: 0 });
			}
		});

		if (highlight) {
			transitionTl.to(
				highlight,
				{
					rotation: highlightRotation,
					svgOrigin: '200 200',
					duration: 0.7,
					ease: 'power2.inOut',
				},
				0
			);
		}

		if (previous !== -1 && copyItems[previous]) {
			transitionTl.to(
				copyItems[previous],
				{ autoAlpha: 0, y: -8, duration: 0.3, ease: 'power2.in' },
				0
			);
		}

		if (copyItems[current]) {
			transitionTl.fromTo(
				copyItems[current],
				{ autoAlpha: 0, y: 8 },
				{ autoAlpha: 1, y: 0, duration: 0.45 },
				0.12
			);
		}

		if (centreImg) {
			transitionTl.fromTo(
				centreImg,
				{ scale: 1 },
				{
					scale: 1.015,
					duration: 0.35,
					yoyo: true,
					repeat: 1,
					ease: 'sine.inOut',
					transformOrigin: '50% 50%',
				},
				0
			);
		}
	};

	const activateFromInteraction = (index) => {
		userStopped = true;
		stopTimer();
		setActive(index);
	};

	segments.forEach((segment, i) => {
		const wedge = segment.querySelector('.animated-steps__wedge');
		if (!wedge) {
			return;
		}

		wedge.addEventListener('click', () => activateFromInteraction(i));
		wedge.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				activateFromInteraction(i);
			}
		});
	});

	desktopMQ.addEventListener('change', () => {
		if (desktopMQ.matches) {
			startTimer();
		} else {
			stopTimer();
		}
	});

	setActive(0, { animate: false });
	startTimer();
};

// Mobile carousel: swipe the scroll-snap track, dots jump to a step and stay in sync.
const initCarousel = (root) => {
	const track = root.querySelector('.animated-steps__slides');
	const slides = Array.from(
		root.querySelectorAll('.animated-steps__slide')
	);
	const bullets = Array.from(
		root.querySelectorAll('.animated-steps__bullet')
	);
	if (!track || slides.length < 2) {
		return;
	}

	const setActive = (index) => {
		bullets.forEach((bullet, i) =>
			bullet.classList.toggle('is-active', i === index)
		);
	};

	bullets.forEach((bullet, i) => {
		bullet.addEventListener('click', () => {
			const slide = slides[i];
			if (slide) {
				slide.scrollIntoView({
					behavior: prefersReducedMotion() ? 'auto' : 'smooth',
					block: 'nearest',
					inline: 'center',
				});
			}
			setActive(i);
		});
	});

	// Keep the active dot in sync while the visitor swipes.
	if ('IntersectionObserver' in window) {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const index = slides.indexOf(entry.target);
						if (index !== -1) {
							setActive(index);
						}
					}
				});
			},
			{ root: track, threshold: 0.6 }
		);
		slides.forEach((slide) => observer.observe(slide));
	}
};

const init = () => {
	document.querySelectorAll(SELECTOR).forEach((root) => {
		initWheel(root);
		initCarousel(root);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
